import * as types from '../services/schedulingCalendarActionType';
import { LOGIN_SUCCESS, LOGOUT_SUCCESS } from '../../login/services/LoginActionType';
import initialState from '../../../store/initialState';

const schedulingCalendarInitialState = initialState.schedulingCalendar;

const schedulingCalendarReducer = (state = schedulingCalendarInitialState, action) => {
    switch (action.type) {
        // A fresh login may belong to a different user/role than whatever was last active in this
        // browser tab — reset filters and cached schedule data so the new session starts clean
        // instead of inheriting (and silently displaying) the previous user's scoped data.
        case LOGIN_SUCCESS:
        case LOGOUT_SUCCESS:
            return schedulingCalendarInitialState;
        case types.FETCH_SCHEDULING_CALENDAR_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
                lastRequestCacheKey: action.payload?.cacheKey || '',
            };
        case types.FETCH_SCHEDULING_CALENDAR_SUCCESS:
            {
                const payloadData = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload?.data;
                const schedules = Array.isArray(payloadData) ? payloadData : [];
                const cacheKey = action.payload?.cacheKey || state.lastRequestCacheKey;

                return {
                    ...state,
                    loading: false,
                    rawSchedules: schedules,
                    cacheByRangeKey: cacheKey
                        ? {
                            ...state.cacheByRangeKey,
                            [cacheKey]: schedules,
                        }
                        : state.cacheByRangeKey,
                };
            }
        case types.FETCH_SCHEDULING_CALENDAR_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload,
            };
        case types.FETCH_SCHEDULING_CALENDAR_METADATA_REQUEST:
            return {
                ...state,
                metadata: {
                    ...state.metadata,
                    loading: true,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_METADATA_SUCCESS:
            return {
                ...state,
                metadata: {
                    loading: false,
                    tasks: action.payload?.tasks || [],
                    radiologists: action.payload?.radiologists || [],
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_METADATA_FAILURE:
            return {
                ...state,
                metadata: {
                    ...state.metadata,
                    loading: false,
                },
            };
        case types.SET_SCHEDULING_CALENDAR_VIEW:
            return {
                ...state,
                view: action.payload,
            };
        case types.SET_SCHEDULING_CALENDAR_SEARCH:
            return {
                ...state,
                searchTerm: action.payload,
            };
        case types.SET_SCHEDULING_CALENDAR_RANGE:
            return {
                ...state,
                range: {
                    ...state.range,
                    ...action.payload,
                },
                // Navigating (prev/next, jump-to-current, month/year picker) must always hit the
                // API for the newly selected period instead of serving a previously cached range.
                cacheByRangeKey: {},
            };
        case types.SET_SCHEDULING_CALENDAR_LIST_STATE:
            return {
                ...state,
                listState: {
                    ...state.listState,
                    ...action.payload,
                },
            };
        case types.SAVE_SCHEDULING_CALENDAR_SHIFT_REQUEST:
            return {
                ...state,
                shiftMutation: {
                    ...state.shiftMutation,
                    saving: true,
                    error: null,
                },
            };
        case types.SAVE_SCHEDULING_CALENDAR_SHIFT_SUCCESS:
            return {
                ...state,
                shiftMutation: {
                    ...state.shiftMutation,
                    saving: false,
                    error: null,
                },
            };
        case types.SAVE_SCHEDULING_CALENDAR_SHIFT_FAILURE:
            return {
                ...state,
                shiftMutation: {
                    ...state.shiftMutation,
                    saving: false,
                    error: action.payload,
                },
            };
        case types.DELETE_SCHEDULING_CALENDAR_SHIFT_REQUEST:
            return {
                ...state,
                shiftMutation: {
                    ...state.shiftMutation,
                    deleting: true,
                    error: null,
                },
            };
        case types.DELETE_SCHEDULING_CALENDAR_SHIFT_SUCCESS:
            return {
                ...state,
                shiftMutation: {
                    ...state.shiftMutation,
                    deleting: false,
                    error: null,
                },
            };
        case types.DELETE_SCHEDULING_CALENDAR_SHIFT_FAILURE:
            return {
                ...state,
                shiftMutation: {
                    ...state.shiftMutation,
                    deleting: false,
                    error: action.payload,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_ADHOC_DROPDOWNS_REQUEST:
            return {
                ...state,
                adhocDropdowns: {
                    ...state.adhocDropdowns,
                    loading: true,
                    error: null,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_ADHOC_DROPDOWNS_SUCCESS:
            return {
                ...state,
                adhocDropdowns: {
                    loading: false,
                    taskTags: action.payload?.taskTags || [],
                    providerTags: action.payload?.providerTags || [],
                    tasks: action.payload?.tasks || [],
                    providers: action.payload?.providers || [],
                    facilityTags: action.payload?.facilityTags || [],
                    facilities: action.payload?.facilities || [],
                    taskLocations: action.payload?.taskLocations || [],
                    worklists: action.payload?.worklists || [],
                    recurrences: action.payload?.recurrences || [],
                    error: null,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_ADHOC_DROPDOWNS_FAILURE:
            return {
                ...state,
                adhocDropdowns: {
                    ...state.adhocDropdowns,
                    loading: false,
                    error: action.payload,
                },
            };
        case types.CHECK_SCHEDULING_CALENDAR_ADHOC_REQUEST:
            return {
                ...state,
                adhocCheck: {
                    loading: true,
                    data: null,
                    error: null,
                },
            };
        case types.CHECK_SCHEDULING_CALENDAR_ADHOC_SUCCESS:
            return {
                ...state,
                adhocCheck: {
                    loading: false,
                    data: action.payload,
                    error: null,
                },
            };
        case types.CHECK_SCHEDULING_CALENDAR_ADHOC_FAILURE:
            return {
                ...state,
                adhocCheck: {
                    loading: false,
                    data: null,
                    error: action.payload,
                },
            };
        case types.SAVE_SCHEDULING_CALENDAR_ADHOC_REQUEST:
            return {
                ...state,
                adhocSave: {
                    saving: true,
                    data: null,
                    error: null,
                },
            };
        case types.SAVE_SCHEDULING_CALENDAR_ADHOC_SUCCESS:
            return {
                ...state,
                adhocSave: {
                    saving: false,
                    data: action.payload,
                    error: null,
                },
            };
        case types.SAVE_SCHEDULING_CALENDAR_ADHOC_FAILURE:
            return {
                ...state,
                adhocSave: {
                    saving: false,
                    data: null,
                    error: action.payload,
                },
            };
        case types.GENERATE_SCHEDULING_CALENDAR_REQUEST:
            return {
                ...state,
                generateSchedules: {
                    loading: true,
                    data: null,
                    error: null,
                },
            };
        case types.GENERATE_SCHEDULING_CALENDAR_SUCCESS:
            return {
                ...state,
                generateSchedules: {
                    loading: false,
                    data: action.payload,
                    error: null,
                },
            };
        case types.GENERATE_SCHEDULING_CALENDAR_FAILURE:
            return {
                ...state,
                generateSchedules: {
                    loading: false,
                    data: null,
                    error: action.payload,
                },
            };
        case types.SET_SCHEDULING_CALENDAR_FILTERS:
            {
                const nextFilters = {
                    ...state.filters,
                    ...action.payload,
                };

                const hasNonUiFilterChange = Object.keys(action.payload || {}).some((key) => {
                    if (key === 'isFilterPanelOpen') {
                        return false;
                    }

                    return state.filters?.[key] !== nextFilters?.[key];
                });

                return {
                    ...state,
                    filters: nextFilters,
                    // Clear cache only when actual filter criteria changes.
                    cacheByRangeKey: hasNonUiFilterChange ? {} : state.cacheByRangeKey,
                };
            }
        case types.CREATE_SCHEDULING_CALENDAR_SHARABLE_LINK_REQUEST:
            return {
                ...state,
                shareLinkCreate: {
                    loading: true,
                    data: null,
                    error: null,
                },
            };
        case types.CREATE_SCHEDULING_CALENDAR_SHARABLE_LINK_SUCCESS:
            return {
                ...state,
                shareLinkCreate: {
                    loading: false,
                    data: action.payload,
                    error: null,
                },
            };
        case types.CREATE_SCHEDULING_CALENDAR_SHARABLE_LINK_FAILURE:
            return {
                ...state,
                shareLinkCreate: {
                    loading: false,
                    data: null,
                    error: action.payload,
                },
            };
        case types.SEND_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_REQUEST:
            return {
                ...state,
                shareLinkEmail: {
                    sending: true,
                    data: null,
                    error: null,
                },
            };
        case types.SEND_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_SUCCESS:
            return {
                ...state,
                shareLinkEmail: {
                    sending: false,
                    data: action.payload,
                    error: null,
                },
            };
        case types.SEND_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_FAILURE:
            return {
                ...state,
                shareLinkEmail: {
                    sending: false,
                    data: null,
                    error: action.payload,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINKS_REQUEST:
            return {
                ...state,
                shareLinkList: {
                    ...state.shareLinkList,
                    loading: true,
                    error: null,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINKS_SUCCESS:
            return {
                ...state,
                shareLinkList: {
                    loading: false,
                    data: Array.isArray(action.payload) ? action.payload : [],
                    error: null,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINKS_FAILURE:
            return {
                ...state,
                shareLinkList: {
                    ...state.shareLinkList,
                    loading: false,
                    error: action.payload,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_AUDIT_REQUEST:
            return {
                ...state,
                shareLinkEmailAudit: {
                    ...state.shareLinkEmailAudit,
                    loading: true,
                    error: null,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_AUDIT_SUCCESS:
            return {
                ...state,
                shareLinkEmailAudit: {
                    loading: false,
                    data: Array.isArray(action.payload) ? action.payload : [],
                    error: null,
                },
            };
        case types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_AUDIT_FAILURE:
            return {
                ...state,
                shareLinkEmailAudit: {
                    ...state.shareLinkEmailAudit,
                    loading: false,
                    error: action.payload,
                },
            };
        case types.DELETE_SCHEDULING_CALENDAR_SHARABLE_LINK_REQUEST:
            return {
                ...state,
                shareLinkDeleteMutation: {
                    deleting: true,
                    error: null,
                },
            };
        case types.DELETE_SCHEDULING_CALENDAR_SHARABLE_LINK_SUCCESS:
            return {
                ...state,
                shareLinkDeleteMutation: {
                    deleting: false,
                    error: null,
                },
            };
        case types.DELETE_SCHEDULING_CALENDAR_SHARABLE_LINK_FAILURE:
            return {
                ...state,
                shareLinkDeleteMutation: {
                    deleting: false,
                    error: action.payload,
                },
            };
        case types.UPDATE_SCHEDULING_CALENDAR_SHARABLE_LINK_DATE_RANGE_REQUEST:
            return {
                ...state,
                shareLinkDateRangeUpdate: {
                    saving: true,
                    error: null,
                },
            };
        case types.UPDATE_SCHEDULING_CALENDAR_SHARABLE_LINK_DATE_RANGE_SUCCESS:
            return {
                ...state,
                shareLinkDateRangeUpdate: {
                    saving: false,
                    error: null,
                },
            };
        case types.UPDATE_SCHEDULING_CALENDAR_SHARABLE_LINK_DATE_RANGE_FAILURE:
            return {
                ...state,
                shareLinkDateRangeUpdate: {
                    saving: false,
                    error: action.payload,
                },
            };
        case types.CLEAR_SCHEDULING_CALENDAR_CREATED_SHARABLE_LINK:
            return {
                ...state,
                shareLinkCreate: {
                    loading: false,
                    data: null,
                    error: null,
                },
            };
        case types.FETCH_TASK_FACILITIES_REQUEST: {
            const { owner, taskId } = action.payload || {};
            if (!owner || !state.taskFacilities[owner]) {
                return state;
            }
            return {
                ...state,
                taskFacilities: {
                    ...state.taskFacilities,
                    [owner]: { loading: true, taskId: taskId ?? null, facilities: [], error: null },
                },
            };
        }
        case types.FETCH_TASK_FACILITIES_SUCCESS: {
            const { owner, taskId, facilities } = action.payload || {};
            if (!owner || !state.taskFacilities[owner]) {
                return state;
            }
            // Ignore stale/out-of-order responses: only accept a response whose taskId still
            // matches the most recently requested taskId for this owner.
            if (state.taskFacilities[owner].taskId !== taskId) {
                return state;
            }
            return {
                ...state,
                taskFacilities: {
                    ...state.taskFacilities,
                    [owner]: { loading: false, taskId, facilities: facilities || [], error: null },
                },
            };
        }
        case types.FETCH_TASK_FACILITIES_FAILURE: {
            const { owner, taskId, error } = action.payload || {};
            if (!owner || !state.taskFacilities[owner]) {
                return state;
            }
            if (state.taskFacilities[owner].taskId !== taskId) {
                return state;
            }
            return {
                ...state,
                taskFacilities: {
                    ...state.taskFacilities,
                    [owner]: { loading: false, taskId, facilities: [], error: error || 'Failed to load task facilities.' },
                },
            };
        }
        case types.CLEAR_TASK_FACILITIES: {
            const { owner } = action.payload || {};
            if (!owner || !state.taskFacilities[owner]) {
                return state;
            }
            return {
                ...state,
                taskFacilities: {
                    ...state.taskFacilities,
                    [owner]: { loading: false, taskId: null, facilities: [], error: null },
                },
            };
        }
        default:
            return state;
    }
};

export default schedulingCalendarReducer;